import React, { useCallback, useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { useTrans } from '../../hooks/useTrans';
import { copyToClipboard } from '../../lib/clipboard';
import ProfileTabs from './components/ProfileTabs';

export default function Security({ authenticatorAppIsConfigured, authenticatorAppConfiguredAt, authenticatorAppRecoveryCodes, securityRoutes }) {
    const translate = useTrans();
    const [copyButtonText, setCopyButtonText] = useState(translate('Kopiëren'));

    const availableCodes = authenticatorAppRecoveryCodes.filter((code) => !code.used_at);

    const getCodesText = useCallback(() => {
        return availableCodes.map((code) => code.code).join('\n');
    }, [availableCodes]);

    function handleCopy() {
        copyToClipboard(getCodesText());
        setCopyButtonText(translate('Gekopieerd!'));
        setTimeout(() => setCopyButtonText(translate('Kopiëren')), 2000);
    }

    function handleDownload() {
        const codes = getCodesText();
        const blob = new Blob([codes], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'recovery-codes.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    function handleRegenerate() {
        router.post(securityRoutes.regenerateRecoveryCodes);
    }

    function handleUnlink() {
        router.post(securityRoutes.unlinkDevice);
    }

    return (
        <div className="max-w-[1200px] mx-auto px-4">
            <ProfileTabs />
            <div className="rounded-[3px] rounded-tl-none border border-[#bbb] bg-white">
                <div className="p-[30px]">
                    <h3 className="text-2xl font-bold text-[#003545] mb-4">
                        {translate('Tweestapsverificatie')}
                    </h3>

                    {authenticatorAppIsConfigured ? (
                        <div>
                            <div className="flex items-start mb-4">
                                <label className="w-[150px] shrink-0 text-sm font-bold text-[#003545]">
                                    {translate('Authenticator-app')}
                                </label>
                                <div>
                                    <span className="text-sm text-[#003545]">
                                        {translate('Gekoppeld op')} {authenticatorAppConfiguredAt}
                                    </span>
                                    <div className="mt-2">
                                        <button
                                            type="button"
                                            onClick={handleUnlink}
                                            className="inline-flex items-center gap-1.5 rounded-sm border border-[#bbb] bg-white px-3 py-1.5 text-sm text-[#003545] hover:bg-[#f5f5f5] cursor-pointer"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 3H10M9 3V10C9 10.5 8.5 11 8 11H3C2.5 11 2 10.5 2 10V3M3.5 3V2C3.5 1.5 4 1 4.5 1H6.5C7 1 7.5 1.5 7.5 2V3M4.5 5.5V8.5M6.5 5.5V8.5" stroke="#DC3939" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span>{translate('Koppeling verwijderen')}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {authenticatorAppRecoveryCodes.length > 0 && (
                                <div className="flex items-start">
                                    <label className="w-[150px] shrink-0 text-sm font-bold text-[#003545]">
                                        {translate('Herstelcodes')}
                                    </label>
                                    <div>
                                        <span className="text-sm text-[#003545]">
                                            {availableCodes.length} {translate('van de')} {authenticatorAppRecoveryCodes.length} {translate('herstelcodes beschikbaar')}
                                        </span>

                                        <div className="mt-4 flex gap-4">
                                            <div className="flex flex-col gap-2">
                                                {authenticatorAppRecoveryCodes.slice(0, 5).map((code, index) => (
                                                    <div
                                                        key={index}
                                                        className={`rounded bg-[#f0f0f0] px-2 py-1 font-mono text-sm ${code.used_at ? 'opacity-70' : ''}`}
                                                    >
                                                        <span className={code.used_at ? 'line-through' : ''}>
                                                            {code.code}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                {authenticatorAppRecoveryCodes.slice(5, 10).map((code, index) => (
                                                    <div
                                                        key={index}
                                                        className={`rounded bg-[#f0f0f0] px-2 py-1 font-mono text-sm ${code.used_at ? 'opacity-70' : ''}`}
                                                    >
                                                        <span className={code.used_at ? 'line-through' : ''}>
                                                            {code.code}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="mt-4 flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={handleCopy}
                                                className="inline-flex items-center gap-1.5 rounded-sm border border-[#bbb] bg-white px-3 py-1.5 text-sm text-[#003545] hover:bg-[#f5f5f5] cursor-pointer"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                </svg>
                                                <span>{copyButtonText}</span>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={handleDownload}
                                                className="inline-flex items-center gap-1.5 rounded-sm border border-[#bbb] bg-white px-3 py-1.5 text-sm text-[#003545] hover:bg-[#f5f5f5] cursor-pointer"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                    <polyline points="7 10 12 15 17 10"></polyline>
                                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                                </svg>
                                                <span>{translate('Downloaden')}</span>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={handleRegenerate}
                                                className="inline-flex items-center gap-1.5 rounded-sm border border-[#bbb] bg-white px-3 py-1.5 text-sm text-[#003545] hover:bg-[#f5f5f5] cursor-pointer"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M23 4v6h-6"></path>
                                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                                                </svg>
                                                <span>{translate('Opnieuw genereren')}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <p className="text-sm text-[#003545] mb-3">
                                {translate('Tweestapsverificatie biedt een extra beveiligingslaag voor je account. Na het koppelen van een authenticator-app moet je, naast je wachtwoord, ook een unieke code invoeren die automatisch wordt gegenereerd door de app.')}
                            </p>
                            <Link
                                href={securityRoutes.linkAuthenticator}
                                className="inline-flex items-center gap-1.5 rounded-sm border border-[#bbb] bg-white px-3 py-1.5 text-sm text-[#003545] hover:bg-[#f5f5f5]"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                </svg>
                                {translate('Authenticator-app koppelen')}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
